import React, { useState, useEffect } from 'react';
import { Box, ColumnLayout, Container, Grid, Header, Link, ProgressBar, SpaceBetween, Badge, Button, Spinner, Modal, Alert } from '@amzn/awsui-components-react';
import { useSelector } from 'react-redux';
import { listServicesWithTracksForQuarter, getNonAmazonAliases } from 'src/utilities/AppSyncPlusAPIs';
import { getQuarterForUpload, getStatus, getOneWBStatusBasedOnCustomAttributes } from './Utility';
import { getDocumentTreeWithTracks, getDocumentFromStore } from './DocumentStoreAPIs';
import { sendFollowUpEmail, cutTicketForFollowUpEmailFailure } from '../../utilities/CloseAPIs';
import appSettings from './app_settings';

const CloseDeliverablesPage = ({ onNavigateTab }) => {
    const currentStage = useSelector((globalState) => globalState.auth.currentStage);

    const [loading, setLoading] = useState(true);
    const [outstandingDeliverables, setOutstandingDeliverables] = useState([]);
    const [overallCompletion, setOverallCompletion] = useState({ completed: 0, total: 0, percentage: 0 });
    const [deliverableCollection, setDeliverableCollection] = useState({ received: 0, total: 0, percentage: 0 });
    const [followUpTarget, setFollowUpTarget] = useState(null);
    const [sendingFollowUp, setSendingFollowUp] = useState(false);
    const [followUpSuccess, setFollowUpSuccess] = useState(false);
    const [followUpRateLimited, setFollowUpRateLimited] = useState(false);
    const [followUpError, setFollowUpError] = useState(null);

    // Map of lowercase alias → external email for registered non-Amazon (subsidiary) recipients.
    // Aliases not in this map fall back to the default "<alias>@amazon.com".
    // Stored in state (not a ref) so the modal re-renders when the map loads.
    const [nonAmazonAliasMap, setNonAmazonAliasMap] = useState({});
    const [aliasMapLoaded, setAliasMapLoaded] = useState(false);

    const appId = appSettings[currentStage]?.appId;
    const quarter = getQuarterForUpload();

    useEffect(() => {
        if (!appId || !quarter) return;
        fetchNonAmazonAliasMap();
        fetchData();
    }, [appId, quarter]);

    const fetchNonAmazonAliasMap = () => {
        getNonAmazonAliases(
            (result) => {
                let data = result?.data?.getNonAmazonAliases;
                if (typeof data === 'string') {
                    try { data = JSON.parse(data); } catch { data = []; }
                }
                const aliasMap = {};
                (data || []).forEach(({ alias, email }) => {
                    if (alias && email) {
                        aliasMap[alias.trim().toLowerCase()] = email.trim();
                    }
                });
                setNonAmazonAliasMap(aliasMap);
                setAliasMapLoaded(true);
            },
            (error) => {
                console.error('Failed to load non-Amazon aliases; all recipients will default to @amazon.com', error);
                setNonAmazonAliasMap({});
                setAliasMapLoaded(true);
            }
        );
    };

    /**
     * Resolve an alias to an email address. Registered non-Amazon aliases resolve
     * to their full external email; everyone else keeps the default Amazon address.
     */
    const resolveEmail = (alias) => {
        if (!alias) return alias;
        if (alias.includes('@')) return alias;
        const key = alias.trim().toLowerCase();
        if (key in nonAmazonAliasMap) {
            return nonAmazonAliasMap[key];
        }
        return `${alias}@amazon.com`;
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [allDeliverables, sections] = await Promise.all([
                fetchDeliverables(),
                fetchSections()
            ]);

            const sectionCountMap = buildSectionCountMap(sections);
            computeOutstandingDeliverables(allDeliverables, sectionCountMap);
            computeOverallCompletion(sections);
            computeDeliverableCollection(allDeliverables);
        } catch (e) {
            console.error('Failed to fetch data for Close Deliverables', e);
            setOutstandingDeliverables([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchDeliverables = () => {
        return new Promise((resolve, reject) => {
            listServicesWithTracksForQuarter(appId, null, quarter, true,
                function onSuccess(result) {
                    let allDeliverables = JSON.parse(result.data.listServicesWithTracksForQuarter);
                    allDeliverables = (allDeliverables || []).sort((a, b) => {
                        if (a.group_name < b.group_name) return -1;
                        if (a.group_name > b.group_name) return 1;
                        return 0;
                    });
                    resolve(allDeliverables);
                },
                function onFailure(error) {
                    reject(error);
                }
            );
        });
    };

    const fetchSections = async () => {
        try {
            const documents = await getDocumentFromStore({ associationId: appId, associationType: 'APP_ID' });
            const documentNames = (documents || []).map(d => d.document_name);
            if (documentNames.length === 0) return [];
            const results = await Promise.all(
                documentNames.map(documentName =>
                    getDocumentTreeWithTracks({
                        quarter,
                        associationId: appId,
                        associationType: 'APP_ID',
                        documentName
                    })
                )
            );
            const allSections = results.reduce((acc, data) => {
                if (Array.isArray(data)) {
                    acc.push(...data);
                }
                return acc;
            }, []);
            return allSections;
        } catch (e) {
            console.error('Failed to fetch sections', e);
            return [];
        }
    };

    const buildSectionCountMap = (sections) => {
        const countMap = {};
        sections.forEach((section) => {
            const serviceMappings = typeof section.service_mappings === 'string'
                ? JSON.parse(section.service_mappings)
                : (section.service_mappings || []);
            serviceMappings.forEach((mapping) => {
                const deliverableId = String(mapping.association_id);
                countMap[deliverableId] = (countMap[deliverableId] || 0) + 1;
            });
        });
        return countMap;
    };

    const getDueDateFromCustomAttributes = (item, attributeType) => {
        const attr = item?.custom_attributes?.find(ca => ca.attribute_type === attributeType);
        if (!attr || isNaN(parseInt(attr.attribute))) return null;
        return new Date(parseInt(attr.attribute));
    };

    const computeOverallCompletion = (sections) => {
        const total = sections.length;
        const completed = sections.filter((section) => {
            const preWbStatus = section.pre_wb_status;
            const postWbStatus = section.post_wb_status;
            return preWbStatus === 'Reviewed' || postWbStatus === 'Reviewed';
        }).length;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        setOverallCompletion({ completed, total, percentage });
    };

    const computeDeliverableCollection = (allDeliverables) => {
        let totalPopulation = 0;
        let received = 0;

        allDeliverables.forEach((item) => {
            const isDisabled = (item?.custom_attributes?.find(
                attr => attr.attribute_type === "DISABLED"
            )?.attribute || "false") === "true";
            if (isDisabled) return;

            const isNoPreWbDueDate = (item?.custom_attributes?.find(
                attr => attr.attribute_type === "NO_PRE_WB_DUE_DATE"
            )?.attribute || "false") === "true";

            const isNoPostWbDueDate = (item?.custom_attributes?.find(
                attr => attr.attribute_type === "NO_POST_WB_DUE_DATE"
            )?.attribute || "false") === "true";

            if (!isNoPreWbDueDate) {
                totalPopulation++;
                const preWBStatus = getStatus(item, getOneWBStatusBasedOnCustomAttributes(item, 'PreWB'));
                if (preWBStatus?.current_status === 'Data Copied to Workiva' || preWBStatus?.current_status === 'Data saved to Workdocs') {
                    received++;
                }
            }

            if (!isNoPostWbDueDate) {
                totalPopulation++;
                const postWBStatus = getStatus(item, getOneWBStatusBasedOnCustomAttributes(item, 'PostWB'));
                if (postWBStatus?.current_status === 'Data Copied to Workiva' || postWBStatus?.current_status === 'Data saved to Workdocs') {
                    received++;
                }
            }
        });

        const percentage = totalPopulation > 0 ? Math.round((received / totalPopulation) * 100) : 0;
        setDeliverableCollection({ received, total: totalPopulation, percentage });
    }
        
    const getAliasesFromField = (item, field) => {
        if (!item?.[field] || item[field].length === 0) return [];
        return item[field].map(entry => entry.alias);
    };

    const calculateOverdueDays = (dueDate, status, today) => {
        if (!dueDate || dueDate >= today || status === 'COMPLETED') return 0;
        return Math.ceil((today - dueDate) / (1000 * 60 * 60 * 24));
    };

    const getProcessUrl = (item) => {
        if (item.flex_id) return `/App/${item.app_id}/flexico/${item.flex_id}/upload`;
        if (item.process_id) return `/App/${item.app_id}/process/${item.process_id}/upload`;
        return null;
    };

    const computeOutstandingDeliverables = (allDeliverables, sectionCountMap) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const overdueItems = [];

        allDeliverables.forEach((item) => {
            const preWBDays = calculateOverdueDays(
                getDueDateFromCustomAttributes(item, 'PRE_WB_DUE_DATE'),
                getStatus(item, getOneWBStatusBasedOnCustomAttributes(item, 'PreWB'))?.overall_status,
                today
            );
            const postWBDays = calculateOverdueDays(
                getDueDateFromCustomAttributes(item, 'POST_WB_DUE_DATE'),
                getStatus(item, getOneWBStatusBasedOnCustomAttributes(item, 'PostWB'))?.overall_status,
                today
            );

            const daysOverdue = Math.max(preWBDays, postWBDays);
            if (daysOverdue === 0) return;

            const owners = getAliasesFromField(item, 'uploaders');
            overdueItems.push({
                id: item.label || item.mapping_id || 'Unknown',
                sections: sectionCountMap[String(item.mapping_id)] || 0,
                owner: owners.length > 0 ? owners[0] : 'Unassigned',
                daysOverdue,
                mappingId: item.mapping_id,
                processUrl: getProcessUrl(item),
                approvers: getAliasesFromField(item, 'approvers'),
            });
        });

        overdueItems.sort((a, b) => {
            if (b.sections !== a.sections) return b.sections - a.sections;
            return b.daysOverdue - a.daysOverdue;
        });

        setOutstandingDeliverables(overdueItems.slice(0, 5));
    };

    const getPercentageColor = (percentage) => {
        if (percentage >= 75) return 'text-status-success';
        if (percentage >= 50) return 'text-status-warning';
        return 'text-status-error';
    }
    
    const getFollowUpRateLimitKey = (deliverableId) => `followup_last_sent_${deliverableId}`;

    const isFollowUpRateLimited = (deliverableId) => {
        try {
            const lastSent = localStorage.getItem(getFollowUpRateLimitKey(deliverableId));
            if (!lastSent) return false;
            const lastSentTime = parseInt(lastSent, 10);
            const now = Date.now();
            const twentyFourHours = 24 * 60 * 60 * 1000;
            return (now - lastSentTime) < twentyFourHours;
        } catch {
            return false;
        }
    };

    const recordFollowUpSent = (deliverableId) => {
        try {
            localStorage.setItem(getFollowUpRateLimitKey(deliverableId), String(Date.now()));
        } catch {
            // localStorage not available, skip
        }
    };

    const handleFollowUpClick = (item) => {
        setFollowUpError(null);
        if (isFollowUpRateLimited(item.mappingId || item.id)) {
            setFollowUpTarget(item);
            setFollowUpRateLimited(true);
            setFollowUpSuccess(false);
        } else {
            setFollowUpTarget(item);
            setFollowUpRateLimited(false);
            setFollowUpSuccess(false);
        }
    };

    const handleConfirmFollowUp = async () => {
        if (!followUpTarget || followUpTarget.owner === 'Unassigned' || followUpRateLimited) return;
        setSendingFollowUp(true);
        setFollowUpError(null);
        try {
            const ownerEmail = resolveEmail(followUpTarget.owner);
            const ccEmails = (followUpTarget.approvers || []).map(a => resolveEmail(a));
            const deliverableLink = followUpTarget.processUrl
                ? `${window.location.origin}${followUpTarget.processUrl}`
                : null;
            const isBeta = currentStage !== 'prod';
            const betaBanner = isBeta
                ? `<div style="background-color: rgb(236, 108, 62); color: white; text-align: center; padding: 8px; font-family: Amazon Ember, sans-serif; font-size: 14px; font-weight: bold;">This is a beta testing environment email</div>`
                : '';
            const subject = `SEC Central Urgent Reminder | ${followUpTarget.owner}'s Deliverable ${followUpTarget.id} is ${followUpTarget.daysOverdue} days overdue`;
            const htmlContent = `<html><body>` +
                betaBanner +
                `<h2 style="font-size:18px; font-weight:700; line-height: 22px; padding:20px 12px; border-bottom:1px solid #EAEDED; font-family:Amazon Ember,sans-serif; text-align: center; color:#000000;">` +
                `SEC Central <span style="color: #eb5f07;">Urgent Reminder</span></h2>` +
                `<div style="padding:20px 12px; font-family:Amazon Ember,sans-serif;">` +
                `<p style="font-size: 14px; color:#000000;">Hi ${followUpTarget.owner},</p>` +
                `<p style="font-size: 14px; color:#000000;">` +
                `This is a reminder that deliverable <strong>${followUpTarget.id}</strong> is currently <strong>${followUpTarget.daysOverdue} days overdue</strong> ` +
                `and is blocking <strong>${followUpTarget.sections} ${followUpTarget.sections === 1 ? 'section' : 'sections'}</strong> from completion.</p>` +
                (deliverableLink ? `<p style="font-size: 14px;"><a href="${deliverableLink}" style="color: #0073bb; text-decoration: none; font-weight: bold;">Click here to take action on this deliverable</a></p>` : '') +
                `<div style="background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px; padding: 15px; margin: 20px 0;">` +
                `<p style="color: #856404; font-weight: bold; margin: 0; font-size: 14px; font-family:Amazon Ember,sans-serif;">` +
                `<strong>IMPORTANT:</strong> Timely uploads are critical for SEC filings (10-Q and 10-K). Any delays may impact the preparation of Amazon's financial statements.</p></div>` +
                `<p style="font-size: 14px; color:#000000;">Please take action to resolve this deliverable at your earliest convenience.</p>` +
                `<p style="font-size: 14px; color:#000000;">Thank you,<br/>SEC Central</p>` +
                `</div></body></html>`;
            const textContent = `Hi ${followUpTarget.owner},\n\n` +
                `This is a reminder that deliverable "${followUpTarget.id}" is currently ${followUpTarget.daysOverdue} days overdue ` +
                `and is blocking ${followUpTarget.sections} ${followUpTarget.sections === 1 ? 'section' : 'sections'} from completion.\n\n` +
                (deliverableLink ? `Take action on this deliverable: ${deliverableLink}\n\n` : '') +
                `IMPORTANT: Timely uploads are critical for SEC filings (10-Q and 10-K). Any delays may impact the preparation of Amazon's financial statements.\n\n` +
                `Please take action to resolve this deliverable at your earliest convenience.\n\n` +
                `Thank you,\nSEC Central`;

            await sendFollowUpEmail({
                toAddresses: [ownerEmail],
                ccAddresses: ccEmails,
                subject,
                textContent,
                htmlContent
            });
            recordFollowUpSent(followUpTarget.mappingId || followUpTarget.id);
            setFollowUpSuccess(true);
        } catch (e) {
            console.error('Failed to send follow up email', e);
            setFollowUpError('Failed to send follow-up email. A ticket has been created for investigation.');
            try {
                await cutTicketForFollowUpEmailFailure({
                    title: `[SEC Central][${currentStage}] Follow-up email failed for deliverable ${followUpTarget.id}`,
                    description: `Follow-up email failed to send.\nDeliverable: ${followUpTarget.id}\nOwner: ${followUpTarget.owner}\nDays Overdue: ${followUpTarget.daysOverdue}\nError: ${e?.message || JSON.stringify(e)}`,
                    severity: 'SEV_3',
                    team: 'ateam-de'
                });
            } catch (ticketErr) {
                console.error('Failed to cut ticket for email failure', ticketErr);
            }
        } finally {
            setSendingFollowUp(false);
        }
    };

    const handleDismissModal = () => {
        setFollowUpTarget(null);
        setFollowUpSuccess(false);
        setFollowUpRateLimited(false);
        setFollowUpError(null);
    };

    if (loading) {
        return (
            <Container>
                <Box textAlign="center" padding="l">
                    <Spinner /> Loading deliverables...
                </Box>
            </Container>
        );
    }

    return (
        <SpaceBetween size="l">
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                <Container>
                    <SpaceBetween size="s">
                        <Box variant="awsui-key-label">Overall Completion</Box>
                        <Box fontSize="display-l" fontWeight="bold" color={getPercentageColor(overallCompletion.percentage)}>
                            {overallCompletion.percentage}%
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                            {overallCompletion.completed} of {overallCompletion.total} sections complete
                        </Box>
                        <ProgressBar value={overallCompletion.percentage} />
                        <Link variant="secondary" href="#" onFollow={(e) => {
                            e.preventDefault();
                            if (onNavigateTab) onNavigateTab('home');
                        }}>
                            View sections →
                        </Link>
                    </SpaceBetween>
                </Container>

                <Container>
                    <SpaceBetween size="s">
                        <Box variant="awsui-key-label">Deliverable Collection</Box>
                        <Box fontSize="display-l" fontWeight="bold" color={getPercentageColor(deliverableCollection.percentage)}>
                            {deliverableCollection.percentage}%
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                            {deliverableCollection.received} of {deliverableCollection.total} deliverables received
                        </Box>
                        <ProgressBar value={deliverableCollection.percentage} />
                        <Link variant="secondary" href="#" onFollow={(e) => {
                            e.preventDefault();
                            if (onNavigateTab) onNavigateTab('deliverables');
                        }}>
                            View deliverables →
                        </Link>
                    </SpaceBetween>
                </Container>
            </Grid>

            <Container
                header={
                    <Header
                        variant="h3"
                        description="Top overdue deliverables blocking section completion"
                    >
                        Top 5 Outstanding Deliverables
                    </Header>
                }
            >
                <SpaceBetween size="m">
                    {outstandingDeliverables.length === 0 ? (
                        <Box textAlign="center" color="text-body-secondary">
                            No outstanding deliverables found.
                        </Box>
                    ) : (
                        outstandingDeliverables.map((item) => (
                            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <SpaceBetween size="xxs">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Link variant="secondary" href={item.processUrl || '#'}>{item.id}</Link>
                                        <Badge color="red">{item.sections} {item.sections === 1 ? 'section' : 'sections'}</Badge>
                                    </div>
                                    <Box variant="small" color="text-body-secondary">Owner: {item.owner}</Box>
                                    <Box variant="small" color="text-status-error">{item.daysOverdue} days overdue</Box>
                                </SpaceBetween>
                                <Button variant="inline-link" onClick={() => handleFollowUpClick(item)}>Follow up</Button>
                            </div>
                        ))
                    )}
                </SpaceBetween>
            </Container>
            
            {followUpTarget && (
                <Modal
                    visible={!!followUpTarget}
                    onDismiss={handleDismissModal}
                    header="Confirm Follow Up"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={handleDismissModal}>Cancel</Button>
                                <Button
                                    variant="primary"
                                    onClick={handleConfirmFollowUp}
                                    loading={sendingFollowUp}
                                    disabled={followUpSuccess || followUpRateLimited || followUpTarget.owner === 'Unassigned' || !aliasMapLoaded}
                                >
                                    {followUpSuccess ? 'Sent' : 'Confirm'}
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween size="m">
                        {followUpRateLimited ? (
                            <Alert type="warning">
                                A follow up automated email has already been triggered for this deliverable in the past 24 hours.
                            </Alert>
                        ) : followUpTarget.owner === 'Unassigned' ? (
                            <Box color="text-status-error">
                                <strong>Unable to send reminder.</strong> No uploader is assigned to this deliverable (<strong>{followUpTarget.id}</strong>). Please assign an uploader before sending a follow-up notification.
                            </Box>
                        ) : followUpSuccess ? (
                            <Box color="text-status-success">
                                <strong>Email sent successfully!</strong> A reminder has been sent to {resolveEmail(followUpTarget.owner)}
                                {followUpTarget.approvers?.length > 0 && ` (CC: ${followUpTarget.approvers.map(a => resolveEmail(a)).join(', ')})`}.
                            </Box>
                        ) : followUpError ? (
                            <Alert type="error">{followUpError}</Alert>
                        ) : (
                            <>
                                <Box>
                                    A reminder email will be sent to <strong>{resolveEmail(followUpTarget.owner)}</strong> regarding the overdue deliverable <strong>{followUpTarget.id}</strong>.
                                </Box>
                                {followUpTarget.approvers?.length > 0 && (
                                    <Box>
                                        The following approvers will be CC'd: <strong>{followUpTarget.approvers.map(a => resolveEmail(a)).join(', ')}</strong>
                                    </Box>
                                )}
                                <Box>
                                    The deliverable is currently <strong>{followUpTarget.daysOverdue} days overdue</strong> and is blocking <strong>{followUpTarget.sections} {followUpTarget.sections === 1 ? 'section' : 'sections'}</strong>.
                                </Box>
                            </>
                        )}
                    </SpaceBetween>
                </Modal>
            )}
        </SpaceBetween>
    );
};

export default CloseDeliverablesPage;
